import React, { useState, useMemo, useEffect } from 'react'
import intl from 'react-intl-universal'
import { useSelector, useDispatch } from 'react-redux'
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  FormGroup,
  Label,
  Input,
} from 'reactstrap'
import { parseISO } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { useAsync } from 'react-async'
import { pick } from 'lodash'

import { DatePickerInput } from '~/reactapp/shared/DatePicker/index'
import LocaleUtils from '~/reactapp/shared/DatePicker/localeUtils'
import { Disclosure } from '~/reactapp/shared/Disclosure/Disclosure'
import { getQuickPickDates } from './quickDates'
import {
  ComposerApiErrorResponse,
  EllipsisReduxStore,
  PubstackRevision,
  PubstackDocument,
  Story,
  Circulation,
} from '~/types'
import { PublishErrorProps, ButtonProps } from './types'
import { errorAction, successAction } from '~/redux/commonActions'
import { publishUnpublishedImages } from '~/modules/services/story/imagePublishing'
import {
  setStoryData,
  setDocumentAction,
  setDraftRevisionAction,
  getCirculation,
  setCirculation,
  setStoryAns,
} from '~/redux/storyData'
import ansToStoryData from '~/reactapp/story/import/ansToStoryData'
import storyCleanup from '~/util/storyCleanup'
import {
  updateDraftRevision,
  publishStoryPubstack,
  getStoryPubstack,
  schedulePublish,
  getAllCirculations,
  resolveUrl,
  saveCirculation,
} from '~/api/story'
import { AppError } from '~/api/AppError'
import usePublishDate from './usePublishDate'
import WrongRevisionModal from '~/reactapp/App/WrongRevisionModal'
import denormalizer from '~/reactapp/story/util/denormalizer'
import { normalizeAnsFromCirculations } from '../CirculateTab/CirculateTab.utils'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const cx = require('./styles.scss')

enum PublishState {
  PUBLISH = 'publishActions.publishModal.buttonPublish',
  UPDATE = 'publishActions.publishModal.buttonUpdate',
  SCHEDULE = 'publishActions.publishModal.buttonSchedule',
}

type Props = PublishErrorProps &
  ButtonProps & {
    scheduledDate?: Date
    onClose: () => void
    fetchScheduledEvents: () => void
  }

export default function PublishModal({
  handleError,
  scheduledDate,
  story,
  onClose,
  fetchScheduledEvents,
}: Props) {
  const isPublished = story.additional_properties?.is_published ?? false

  const { timezone: tz, quickPickTimes, clockFormat } = useSelector(
    (state: EllipsisReduxStore) => ({
      timezone: state.settings.timezone,
      quickPickTimes: state.settings.quickPickTimes,
      clockFormat: state.settings.clockFormat,
    })
  )
  const reduxCirculations = useSelector<EllipsisReduxStore, Circulation[]>(
    (state) => getCirculation(state)
  )
  const siteServiceWebsites = useSelector(
    (state: EllipsisReduxStore) => state.websites
  )
  const dispatch = useDispatch()

  const {
    date: selectedPublishDate,
    setDate: setSelectedPublishDate,
    isDateNow: isPublishingNow,
    relativeNow,
  } = usePublishDate(
    // In a draft version of a story, the publish_date can be:
    // 1. scheduled publish date if it has been scheduled
    // 2. current publish date
    // 3. missing or old dates since only the published edition is guaranteed to have it, not the draft version we fetched from the Story API
    // So explicitly use the additional_properties.publish_date and publish_date (fallback) here
    scheduledDate
      ? LocaleUtils.relativeDate(scheduledDate)
      : isPublished
      ? LocaleUtils.relativeDate(
          parseISO(
            (story.additional_properties?.publish_date ||
              story.publish_date) as string
          )
        )
      : undefined
  )
  const [expectedRevisionId, setExpectedRevisionId] = useState<string>()
  const [selectedDisplayDate, setSelectedDisplayDate] = useState(
    story.display_date
      ? LocaleUtils.relativeDate(parseISO(story.display_date))
      : relativeNow
  )
  const [linkDisplayTime, setLinkDisplayTime] = useState(
    !(scheduledDate || isPublished)
  )
  const quickPickDates = useMemo(
    () => getQuickPickDates(story, quickPickTimes, relativeNow, 'scheduled'),
    [story, quickPickTimes, relativeNow]
  )

  // Conditionally update display date when publish date changes
  useEffect(() => {
    if (linkDisplayTime) {
      setSelectedDisplayDate(selectedPublishDate)
    }
  }, [linkDisplayTime, selectedPublishDate])

  // Link display time after unpublishing or unscheduling
  useEffect(() => {
    if (selectedDisplayDate === undefined) {
      setLinkDisplayTime(true)
    }
  }, [selectedDisplayDate])

  const getPublishState = () => {
    if (!isPublishingNow) {
      return PublishState.SCHEDULE
    }

    return isPublished || scheduledDate
      ? PublishState.UPDATE
      : PublishState.PUBLISH
  }

  async function generatePreviewUrls(revision: PubstackRevision) {
    const circulationsWithoutUrls: Record<string, Circulation> = {}
    for (const circulation of reduxCirculations) {
      if (!circulation.website_url) {
        circulationsWithoutUrls[circulation.website_id] = circulation
      }
    }

    if (!Object.keys(circulationsWithoutUrls).length) {
      return
    }

    const normalizedAns = normalizeAnsFromCirculations(
      revision.ans as Story,
      reduxCirculations,
      selectedPublishDate
    )
    if (!normalizedAns) {
      handleError('websiteURLsGenerationFailure"')
      console.error('Failed to normalize ANS from circulations.')
      return
    }
    const [ans] = await denormalizer([normalizedAns]).catch((error) => {
      console.error(
        'Failed to denormalize ANS before generating preview URLs',
        error
      )
      return normalizedAns
    })

    const resolveUrlResults = await Promise.allSettled(
      Object.values(circulationsWithoutUrls).map((circ) => {
        return resolveUrl(circ.website_id, revision.document_id, ans)
          .then((resolveUrlResult) => {
            return {
              websiteId: circ.website_id,
              ...resolveUrlResult,
            }
          })
          .catch((error) => {
            delete circulationsWithoutUrls[circ.website_id]
            throw new Error(
              `${siteServiceWebsites[circ.website_id].display_name} - ${
                error.errorData.error_message
              }`
            )
          })
      })
    )

    const failedWebsites: string[] = []
    for (const result of resolveUrlResults) {
      if (result.status === 'fulfilled') {
        const { websiteId, website_url } = result.value
        circulationsWithoutUrls[websiteId].website_url = website_url
      } else {
        failedWebsites.push(result.reason.message)
      }
    }

    if (failedWebsites.length) {
      dispatch(
        errorAction({
          toastConfig: {
            message: failedWebsites.join('<br/>'),
            title: intl.get('PublishActions.resolveUrlFailed'),
            optionsOverride: {
              allowHtml: true,
              timeOut: 30000,
            },
          },
        })
      )
    }

    const circulationsToUpdate = Object.values(circulationsWithoutUrls)
    if (circulationsToUpdate.length) {
      try {
        await Promise.all(
          Object.values(circulationsWithoutUrls).map((circ) => {
            return saveCirculation(circ, {
              collision_behavior: 'increment',
            })
          })
        )
        const updatedCirculations = await getAllCirculations(story._id)
        dispatch(setCirculation(updatedCirculations))
      } catch (error) {
        dispatch(
          errorAction({
            message: intl.get('PublishActions.savePreviewUrlFailed'),
          })
        )
      }
    }
  }

  const { run, isLoading } = useAsync<{
    story: Story
    document?: PubstackDocument
    revision?: PubstackRevision
    circulations?: Circulation[]
  }>({
    watch: story,
    deferFn: async ([payloadStory]) => {
      const publishDate = zonedTimeToUtc(selectedPublishDate, tz).toISOString()
      const displayDate = zonedTimeToUtc(selectedDisplayDate, tz).toISOString()
      const bodyWithCleanedStory = {
        // Let the server set the display time by omitting it in the request payload
        ...(linkDisplayTime === false
          ? {
              displayDate,
            }
          : {}),
        ...(!isPublishingNow
          ? {
              publishDate,
            }
          : {}),
        story: storyCleanup(payloadStory, reduxCirculations),
      }

      if (isPublishingNow) {
        // Publish
        const pubstackPublishResult = await publishStoryPubstack(
          bodyWithCleanedStory,
          dispatch
        )
        const updatedCirculations = await getAllCirculations(
          bodyWithCleanedStory.story._id
        )

        return {
          circulations: updatedCirculations,
          story: pubstackPublishResult.legacyStory,
          document: pubstackPublishResult.document,
          revision: pubstackPublishResult.revision,
        }
      } else {
        // Schedule Publish
        // Save story first to update display date before scheduling
        const updateRevisionResult = await updateDraftRevision(
          bodyWithCleanedStory.story._id,
          {
            ...bodyWithCleanedStory.story,
            // eslint-disable-next-line @typescript-eslint/camelcase
            display_date: displayDate,
          },
          dispatch
        )

        await schedulePublish(
          bodyWithCleanedStory.story._id,
          publishDate,
          displayDate
        )

        return {
          story: updateRevisionResult.legacyStory,
          document: updateRevisionResult.document,
          revision: updateRevisionResult.revision,
        }
      }
    },
    onResolve: async (data) => {
      const resultStory = {
        ...story,
        ...pick(data.story, [
          'display_date',
          'publish_date',
          'websites',
          'canonical_url',
          'short_url',
          'revision',
          'last_updated_date',
          'first_publish_date',
        ]),
        /* eslint-disable @typescript-eslint/camelcase */
        additional_properties: {
          ...data.story.additional_properties,
          // If story is already published, it remains as published
          is_published: isPublished || isPublishingNow,
        },
        isDirty: false,
      }
      dispatch(
        setStoryAns({
          storyAns: resultStory,
          // Unset isDirty after a publish/schedule action
          isDirty: false,
        })
      )
      // since we're not using Angular save() on v2/publish, manually update redux
      dispatch(setStoryData(ansToStoryData(resultStory)))

      if (data.document && data.revision) {
        dispatch(setDocumentAction(data.document))
        dispatch(setDraftRevisionAction(data.revision))
      }
      if (data.circulations) {
        dispatch(setCirculation(data.circulations))
      }

      dispatch(
        successAction(
          isPublishingNow
            ? intl.get('publishActions.commitPublicationSuccess.published')
            : intl.get('publishActions.commitPublicationSuccess.scheduled')
        )
      )

      if (!isPublishingNow) {
        if (data.revision) {
          await generatePreviewUrls(data.revision)
        }

        fetchScheduledEvents()
      }

      onClose()
    },
    onReject: (error) => {
      console.error('PublishModal error', error)

      if (error instanceof AppError) {
        // Pubstack endpoint error
        if (error.status === 412) {
          setExpectedRevisionId('dummy-value-to-show-modal')
          return
        } else if (
          error.status === 403 &&
          error.errorKey &&
          ['noAccessPerm', 'authorNotFound'].includes(error.errorKey)
        ) {
          // Freelancer tried to publish but they aren't an author
          handleError(intl.get('errorReasons.errorReasons.noAccessPerm'))
          return
        }

        handleError(error.errorKey as string)
      } else {
        const {
          errorKey,
          errorData,
          statusCode,
        } = ((error as unknown) as ComposerApiErrorResponse).error

        if (statusCode === 422) {
          setExpectedRevisionId((errorData as { expected: string }).expected)
          return
        } else if (statusCode === 403) {
          console.log(errorKey, errorData)
        }

        handleError(errorKey)
      }
    },
  })

  const handleStoryUpdate = async () => {
    try {
      const publishImagesResponse = await publishUnpublishedImages(story)

      if (publishImagesResponse.unpublishedImagesFound) {
        if (!publishImagesResponse.hasPPPermission) {
          dispatch(errorAction({ message: publishImagesResponse.message }))
        } else {
          dispatch(successAction(publishImagesResponse.message))
        }
      }

      run(story)
    } catch (error) {
      dispatch(
        errorAction({
          message: intl.get('publishActions.publishImages.error'),
        })
      )
    }
  }

  function toggleWrongRevisionModal() {
    setExpectedRevisionId(undefined)
  }

  const tzBlurb =
    tz === Intl.DateTimeFormat().resolvedOptions().timeZone
      ? intl.get('publishActions.publishModal.systemTimezone', {
          systemTimezone: tz,
        })
      : intl.get('publishActions.publishModal.orgTimezone', {
          orgTimezone: tz,
          systemTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })

  return (
    <>
      <WrongRevisionModal
        action="publish"
        isOpen={Boolean(expectedRevisionId)}
        toggle={toggleWrongRevisionModal}
        onConfirm={async () => {
          toggleWrongRevisionModal()
          await getStoryPubstack(story._id) // Get latest ETag
          run(story)
        }}
      />

      <Modal isOpen toggle={onClose} className="bootstrap4">
        <ModalHeader toggle={onClose}>
          {isPublished
            ? intl.get('publishActions.publishModal.update')
            : intl.get('publishActions.publishModal.publish')}
        </ModalHeader>
        <ModalBody>
          <p className={cx.blurb}>{tzBlurb}</p>
          <div className="form-group">
            <label htmlFor="publish-modal-publish-date">
              <span>{intl.get('publishActions.publishModal.publishTime')}</span>
            </label>
            <DatePickerInput
              inputId="publish-modal-publish-date"
              value={selectedPublishDate}
              onChange={setSelectedPublishDate}
              quickDates={quickPickDates}
              disabledDays={{ before: relativeNow }}
              use24Hour={clockFormat === '24'}
            />
          </div>

          <Disclosure
            label={intl.get('publishActions.publishModal.showDisplayTime')}
            closeLabel={intl.get('publishActions.publishModal.hideDisplayTime')}
            isOpen={Boolean(isPublished || scheduledDate)}
            className={cx.displayTime}
          >
            <div>
              <label htmlFor="publish-modal-display-date">
                {intl.get('publishActions.publishModal.displayTime')}
                <i
                  className="fa fa-info-circle fa-lg"
                  data-toggle="tooltip"
                  data-placement="right"
                  data-original-title={intl.get(
                    'publishActions.publishModal.displayTimeTitle'
                  )}
                  title={intl.get(
                    'publishActions.publishModal.displayTimeTitle'
                  )}
                  style={{ marginLeft: '5px' }}
                />
              </label>
              <DatePickerInput
                inputId="publish-modal-display-date"
                value={selectedDisplayDate}
                onChange={(val) => {
                  setLinkDisplayTime(false)
                  setSelectedDisplayDate(val)
                }}
                use24Hour={clockFormat === '24'}
                quickDates={[
                  relativeNow,
                  ...(isPublished && story.display_date
                    ? [
                        {
                          date: selectedDisplayDate,
                          label: intl.get(
                            'publishActions.publishModal.boltCurrent'
                          ),
                        },
                      ]
                    : []),
                ]}
              />
              <FormGroup check inline>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={linkDisplayTime}
                    onChange={() => setLinkDisplayTime((linked) => !linked)}
                  />
                  {intl.get('publishActions.publishModal.matchDisplayTime')}
                </Label>
              </FormGroup>
            </div>
          </Disclosure>
        </ModalBody>

        <ModalFooter>
          <Button outline={true} onClick={onClose}>
            {intl.get('publishActions.publishModal.buttonCancel')}
          </Button>
          <Button
            data-testid="publish"
            onClick={handleStoryUpdate}
            disabled={isLoading}
            color={
              getPublishState() === PublishState.PUBLISH ? 'success' : 'primary'
            }
          >
            {intl.get(getPublishState())}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}
